// usePWAInstall.ts - Hook for PWA installation detection and management
import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'ios' | 'android' | 'desktop';

interface PWAInstallState {
  isInstalled: boolean;
  canInstall: boolean;
  platform: Platform;
  showPrompt: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
}

const STORAGE_KEYS = {
  DISMISSED: 'whipt_pwa_dismissed',
  INSTALLED: 'whipt_pwa_installed',
  LAST_SHOWN: 'whipt_pwa_last_shown',
  DISMISS_COUNT: 'whipt_pwa_dismiss_count',
};

export const usePWAInstall = () => {
  const [state, setState] = useState<PWAInstallState>({
    isInstalled: false,
    canInstall: false,
    platform: 'desktop',
    showPrompt: false,
    deferredPrompt: null,
  });

  // Detect platform
  const detectPlatform = (): Platform => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'desktop';
  };

  // Check if already installed
  const checkIfInstalled = (): boolean => {
    // Check display mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    // Check iOS standalone
    const isIOSStandalone = (window.navigator as any).standalone === true;
    // Check localStorage flag
    const wasInstalled = localStorage.getItem(STORAGE_KEYS.INSTALLED) === 'true';
    
    const installed = isStandalone || isIOSStandalone || wasInstalled;
    console.log('[PWA Hook] Install check:', { isStandalone, isIOSStandalone, wasInstalled, installed });
    return installed;
  };

  // Check if should show prompt
  const shouldShowPrompt = (): boolean => {
    // Don't show if permanently dismissed
    const permanentDismiss = localStorage.getItem(STORAGE_KEYS.DISMISSED);
    if (permanentDismiss === 'permanent') {
      console.log('[PWA Hook] Permanently dismissed');
      return false;
    }

    // Don't show if dismissed more than 3 times
    const dismissCount = parseInt(localStorage.getItem(STORAGE_KEYS.DISMISS_COUNT) || '0');
    if (dismissCount >= 3) {
      console.log('[PWA Hook] Dismissed too many times:', dismissCount);
      return false;
    }

    // Check if shown recently (7 day rate limit)
    const lastShown = localStorage.getItem(STORAGE_KEYS.LAST_SHOWN);
    if (lastShown) {
      const daysSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) {
        console.log('[PWA Hook] Shown recently:', daysSince.toFixed(2), 'days ago');
        return false;
      }
    }

    console.log('[PWA Hook] Should show prompt: true');
    return true;
  };

  // Initialize state
  useEffect(() => {
    console.log('[PWA Hook] Initializing...');
    const platform = detectPlatform();
    const isInstalled = checkIfInstalled();
    const showPrompt = !isInstalled && shouldShowPrompt();

    console.log('[PWA Hook] State:', { platform, isInstalled, showPrompt });

    setState(prev => ({
      ...prev,
      platform,
      isInstalled,
      showPrompt,
    }));

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      
      setState(prev => ({
        ...prev,
        deferredPrompt: promptEvent,
        canInstall: true,
      }));
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('✅ PWA installed successfully!');
      localStorage.setItem(STORAGE_KEYS.INSTALLED, 'true');
      setState(prev => ({
        ...prev,
        isInstalled: true,
        showPrompt: false,
      }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Trigger native install prompt
  const install = async (): Promise<boolean> => {
    if (!state.deferredPrompt) {
      return false;
    }

    try {
      await state.deferredPrompt.prompt();
      const { outcome } = await state.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem(STORAGE_KEYS.INSTALLED, 'true');
        setState(prev => ({
          ...prev,
          isInstalled: true,
          showPrompt: false,
          deferredPrompt: null,
        }));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Install prompt failed:', error);
      return false;
    }
  };

  // Dismiss prompt temporarily
  const dismissTemporarily = () => {
    localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, Date.now().toString());
    const currentCount = parseInt(localStorage.getItem(STORAGE_KEYS.DISMISS_COUNT) || '0');
    localStorage.setItem(STORAGE_KEYS.DISMISS_COUNT, (currentCount + 1).toString());
    
    setState(prev => ({ ...prev, showPrompt: false }));
  };

  // Dismiss prompt permanently
  const dismissPermanently = () => {
    localStorage.setItem(STORAGE_KEYS.DISMISSED, 'permanent');
    setState(prev => ({ ...prev, showPrompt: false }));
  };

  // Mark as shown (for rate limiting)
  const markAsShown = () => {
    localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, Date.now().toString());
  };

  return {
    ...state,
    install,
    dismissTemporarily,
    dismissPermanently,
    markAsShown,
  };
};
